import axios from "axios";
import { disclosurePayload } from "@/components/mcp/mcpDisclosure";

// AI Assistant (MCP) settings — BE routes src/app/dent_cloud/api/routes/mcpSetting (spec §12).
// Settings are edited in round-trips, so only `me` (profile-menu visibility) is cached per session.
// Mutating calls opt out of the global error toast; components show mcpErrorMessage() inline.
const QUIET = { skipGlobalErrorHandler: true };
// /api/mcpSetting/... calls opt out of the axios 403 permission-refresh +
// redirect-to-/clinic flow — a partial manager gets a 403 here routinely
// (only C-Level / all-branch managers may manage AI Assistant), and the
// component shows its own inline "access denied" message instead.
const NO_REDIRECT = { skipPermissionRedirect: true };

let meRequest = null;

const moduleMcpSetting = {
  namespaced: true,
  state: {
    me: null,
  },
  mutations: {
    setMe(state, me) {
      state.me = me;
    },
  },
  actions: {
    async fetchSetting(_ctx, { clinicUrl }) {
      const res = await axios.get(`/api/mcpSetting/${clinicUrl}`, NO_REDIRECT);
      return res.data.data;
    },
    async updateEnabled(_ctx, { clinicUrl, enabled }) {
      const res = await axios.put(`/api/mcpSetting/${clinicUrl}`, { enabled }, { ...QUIET, ...NO_REDIRECT });
      return res.data.data;
    },
    async acceptDpa(_ctx, { clinicUrl, version }) {
      const res = await axios.post(`/api/mcpSetting/${clinicUrl}/dpa`, { version }, { ...QUIET, ...NO_REDIRECT });
      return res.data.data;
    },
    async updatePatientDisclosure(_ctx, { clinicUrl, mode, acknowledged }) {
      const res = await axios.put(
        `/api/mcpSetting/${clinicUrl}/patientDisclosure`,
        disclosurePayload(mode, acknowledged),
        { ...QUIET, ...NO_REDIRECT }
      );
      return res.data.data;
    },
    async fetchProcedureGroups(_ctx, { clinicUrl }) {
      const res = await axios.get(`/api/mcpSetting/${clinicUrl}/procedureGroups`, NO_REDIRECT);
      return res.data.data;
    },
    async saveProcedureGroups(_ctx, { clinicUrl, procedureGroups }) {
      const res = await axios.put(
        `/api/mcpSetting/${clinicUrl}/procedureGroups`,
        { procedureGroups },
        { ...QUIET, ...NO_REDIRECT }
      );
      return res.data.data;
    },
    async fetchUsage(_ctx, { clinicUrl }) {
      const res = await axios.get(`/api/mcpSetting/${clinicUrl}/usage`, { ...QUIET, ...NO_REDIRECT });
      return res.data.data;
    },
    async fetchConnections(_ctx, { clinicUrl, includeRevoked = false }) {
      const res = await axios.get(`/api/mcpSetting/${clinicUrl}/connections`, {
        params: includeRevoked ? { includeRevoked: true } : {},
        ...NO_REDIRECT,
      });
      return res.data.data;
    },
    async revokeConnection(_ctx, { clinicUrl, connectionId }) {
      const res = await axios.delete(
        `/api/mcpSetting/${clinicUrl}/connections/${connectionId}`,
        { ...QUIET, ...NO_REDIRECT }
      );
      return res.data.data;
    },
    async fetchAuditLogs(_ctx, { clinicUrl, params }) {
      const res = await axios.get(`/api/mcpSetting/${clinicUrl}/auditLogs`, { params, ...QUIET, ...NO_REDIRECT });
      return res.data.data;
    },
    async fetchMe({ state, commit }, { force = false } = {}) {
      if (!force && state.me) return state.me;
      if (!force && meRequest) return meRequest;
      meRequest = axios
        .get(`/api/mcp/me`, QUIET)
        .then(res => {
          commit("setMe", res.data.data);
          return res.data.data;
        })
        .finally(() => {
          meRequest = null;
        });
      return meRequest;
    },
    async revokeMyConnection({ dispatch }, { connectionId }) {
      await axios.delete(`/api/mcp/me/connections/${connectionId}`, QUIET);
      return dispatch("fetchMe", { force: true });
    },
    // Ruling C6: called from moduleUser/logout so the next login in the same tab
    // never shows the previous user's "AI Assistant" profile-menu entry.
    resetMe({ commit }) {
      meRequest = null;
      commit("setMe", null);
    },
  },
  getters: {
    me: state => state.me,
    hasAiAssistantAccess: state =>
      Boolean(
        state.me &&
          ((Array.isArray(state.me.clinics) && state.me.clinics.length > 0) ||
            (Array.isArray(state.me.connections) && state.me.connections.length > 0))
      ),
  },
};

export default moduleMcpSetting;
